import { useMemo } from "react";
import { useLocation } from "react-router-dom";

import { useProfile } from "@/context/ProfileContext";
import { useViewAs } from "@/context/ViewAsContext";
import { applyViewAs } from "@/config/view-as";
import { useRemoteConfigBoolean } from "@/context/RemoteConfigContext";
import { usePulseCheckInEnabled } from "@/hooks/use-pulse-check-in-enabled";
import {
  filterSidebarNavFeatures,
  getActiveSidebarFeatureId,
  type SidebarNavFeature,
} from "@/config/sidebar-nav";

/**
 * The nav features the signed-in member may see, resolved once from the
 * profile and remote-config gates. Every nav surface (sidebar, mobile tabs,
 * More, ⌘K, Settings sub-nav, section tabs) reads this so they never disagree.
 */
export function useVisibleSidebarNavFeatures(): SidebarNavFeature[] {
  const {
    businessProfile,
    memberAccessLevel,
    memberRole,
    memberPermissions,
    memberStoreRole,
  } = useProfile();
  const showIntelligence = useRemoteConfigBoolean("show_intelligence");
  const showPulseCheck = usePulseCheckInEnabled();
  const { effectiveRole } = useViewAs();

  return useMemo(
    () =>
      applyViewAs(
        filterSidebarNavFeatures({
          businessProfile,
          remoteConfig: { showIntelligence, showPulseCheck },
          memberAccessLevel,
          memberRole,
          memberPermissions,
          memberStoreRole,
        }),
        effectiveRole,
      ),
    [
      businessProfile,
      memberAccessLevel,
      memberRole,
      memberPermissions,
      memberStoreRole,
      showIntelligence,
      showPulseCheck,
      effectiveRole,
    ],
  );
}

/** One visible feature by id, or undefined when the member can't see it. */
export function useSidebarNavFeature(
  id: string,
): SidebarNavFeature | undefined {
  const features = useVisibleSidebarNavFeatures();
  return useMemo(() => features.find((f) => f.id === id), [features, id]);
}

/**
 * The nav group the current route belongs to (the row that lights in the
 * sidebar), or undefined on company-wide pages such as Settings.
 */
export function useActiveSidebarNavFeature(): SidebarNavFeature | undefined {
  const features = useVisibleSidebarNavFeatures();
  const { pathname } = useLocation();
  return useMemo(() => {
    const id = getActiveSidebarFeatureId(
      features.filter((f) => !f.hiddenFromNav),
      pathname,
    );
    return features.find((f) => f.id === id);
  }, [features, pathname]);
}
